import { Box, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BUDGET, BudgetExpenseOrIncome } from 'business/constants';
import Loading from 'components/Loading';
import React, { ComponentProps, useEffect } from 'react';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { useGetEditableValuesByBudgetResource } from 'routes/BudgetModule/helpers/hooks/writeScopes';
import { useTranslation } from 'react-i18next';
import { translateBlockValue, translateBlock } from 'i18n';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { useParams } from 'react-router-dom';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import { useGetIntegrations } from 'helpers/integrations';
import Flex from 'ui/Flex/Flex';
import GenericAutocomplete from 'ui/DropdownItems/GenericAutocomplete';
import { capitalize } from 'lodash';
import { AutocompletAble } from 'ui/DropdownItems/types';
import NotFound from 'components/Errors/NotFound';
import ProjectionBanner from './Components/ProjectionBanner';
import { useGetProjection } from './hooks/projection';
import AtterrissageGrid from './Components/AtterrissageGrid';
import { tagFromProposal } from '../../helpers/eventHelpers';
import PluriannualProjectionGrid from './Components/PluriannualProjectionGrid';
import {
    getCustomFilterLabel,
    isCustomBudgetProposal,
    useGetCustomFilters,
} from '../hooks/budgetProposal';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import { canEditResource } from 'routes/BudgetModule/helpers/writeScopesHelpers';
import {
    exportGridsToExcel,
    getExcelFilename,
    getExpenseIncomeLabel,
    PROJECTION_FILE_PREFIX,
} from 'routes/BudgetModule/contexts/helpers';
import { useFlatExport } from 'routes/BudgetModule/helpers/hooks/useFlatExport';
import ExportButtonMultipleGrid from 'components/ExportButton/ExportButtonMultipleGrid';
import { useGridApiContext } from 'routes/BudgetModule/contexts/GridApiContext';

type Props = {
    budgetProposal: BudgetProposalT;
    resourceName: string;
    resourceSoftwareId: string;
};

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})(({ theme }) => ({
    color: theme.palette.primary.main,
}));

const StyContentWrapper = styled(Box, {
    name: 'StyContentWrapper',
})({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '1rem',
    marginLeft: '2rem',
    marginRight: '2rem',
});

const StyGrids = styled(Box, {
    name: 'StyGrids',
})({
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    width: '100%',
    marginTop: '2em',
});

const Projection = ({
    budgetProposal,
    resourceName,
    resourceSoftwareId,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const {
        budgetProposalId,
        resourceSoftwareId: resourceSoftwareIdParam,
        projectionType,
    } = useParams<{
        budgetProposalId: string;
        resourceSoftwareId: string;
        projectionType: 'atterrissage' | 'pluriannual';
    }>();

    useEffect(() => {
        let trackEvent: string | null = null;
        if (projectionType === 'atterrissage') {
            trackEvent =
                TRACKABLE_EVENTS.budget.budgetProposal.projection.atterrissage
                    .pageOpened;
        } else if (projectionType === 'pluriannual') {
            trackEvent =
                TRACKABLE_EVENTS.budget.budgetProposal.projection.pluriannual
                    .pageOpened;
        }
        if (trackEvent !== null) {
            trackEventBudget(trackEvent, {
                ...tagFromProposal(budgetProposal),
                resource: resourceName,
            });
        }
    }, [projectionType]);

    const {
        data: editableValuesByResource,
        isLoading: isEditableValuesLoading,
    } = useGetEditableValuesByBudgetResource();
    const [gridContext] = useGridApiContext();

    const { data: projection, isLoading: isProjectionLoading } =
        useGetProjection(budgetProposal.id, resourceSoftwareId);

    const { data: integrations, isLoading: isIntegrationLoading } =
        useGetIntegrations();

    const isLoading =
        isProjectionLoading || isIntegrationLoading || isEditableValuesLoading;

    const { code, setCodes, codeOptions } = useGetCustomFilters(integrations);

    const isSchool = isCustomBudgetProposal(integrations);

    const fileName = getExcelFilename(PROJECTION_FILE_PREFIX, [
        projectionType || 'atterrissage',
    ]);
    const exportFunction = () =>
        exportGridsToExcel(gridContext[fileName], fileName);
    const { shouldShowOpenedGroups, setShouldShowOpenedGroups } =
        useFlatExport(exportFunction);

    if (resourceSoftwareIdParam === undefined) {
        return <NotFound />;
    }
    const resourceSoftwareIdEncoded = encodeURIComponent(
        resourceSoftwareIdParam,
    );
    const commonPath = `/budget_proposal/${budgetProposalId}/resource/${resourceSoftwareIdEncoded}/projection`;

    const canEditProjection = canEditResource(
        editableValuesByResource,
        resourceSoftwareId,
        budgetProposal,
    );

    const exportButton = (
        <ExportButtonMultipleGrid
            fileName={fileName}
            setShouldShowOpenedGroups={setShouldShowOpenedGroups}
        />
    );

    const setCodesAndTrack = (codes: AutocompletAble<string> | null) => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.projection.atterrissage
                .filterBudgetLineCode,
            { code: codes?.label },
        );
        setCodes(codes);
    };
    const tabsList = [
        {
            label: t('projection.atterrissageProjection'),
            pathname: `${commonPath}/atterrissage`,
            component: AtterrissageGrid,
            componentProps: {
                budgetProposal,
                resourceSoftwareId,
                code: code?.value,
                shouldShowOpenedGroups,
            } as Omit<
                ComponentProps<typeof AtterrissageGrid>,
                'expenseOrIncome'
            >,
            statusField: 'atterrissageStatus' as const,
            canEditProjection,
        },
        {
            label: t('projection.pluriannualProjection'),
            pathname: `${commonPath}/pluriannual`,
            component: PluriannualProjectionGrid,
            componentProps: {
                budgetProposal,
                resourceSoftwareId,
                code: code?.value,
                shouldShowOpenedGroups,
            } as Omit<
                ComponentProps<typeof PluriannualProjectionGrid>,
                'expenseOrIncome'
            >,
            statusField: 'pluriannualProjectionStatus' as const,
            canEditProjection:
                canEditProjection &&
                (budgetProposal.canEditPluriannualProjections ?? true),
        },
    ].map((tabItem) => ({
        label: tabItem.label,
        pathname: tabItem.pathname,
        key: tabItem.label,
        disabled: false,
        tabActions: (
            <Flex justifyContent="flex-end" width="100%" alignItems="center">
                {exportButton}
            </Flex>
        ),
        children:
            isLoading || isIntegrationLoading ? (
                <Loading />
            ) : (
                <StyContentWrapper>
                    <ProjectionBanner
                        projection={projection!}
                        resourceSoftwareId={resourceSoftwareId}
                        budgetProposal={budgetProposal}
                        canEditProjection={tabItem.canEditProjection}
                        resourceName={resourceName}
                        statusField={tabItem.statusField}
                    />
                    {isSchool && (
                        <Flex width="100%" justifyContent="flex-end">
                            <GenericAutocomplete
                                selectedValues={code}
                                availableValues={codeOptions}
                                setSelectedValues={setCodesAndTrack}
                                label={capitalize(
                                    translateBlock(
                                        getCustomFilterLabel(integrations),
                                    ),
                                )}
                                sx={{
                                    m: '0',
                                    width: '30%',
                                    mt: '1rem',
                                }}
                            />
                        </Flex>
                    )}
                    <StyGrids>
                        {[
                            BudgetExpenseOrIncome.EXPENSE,
                            BudgetExpenseOrIncome.INCOME,
                        ].map((expIncome) => {
                            const GridComponent = tabItem.component;
                            return (
                                <Box key={expIncome}>
                                    <Card elevation={1}>
                                        <FullScreenProvider module={BUDGET}>
                                            <CardContent>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        paddingBottom: '1em',
                                                        gap: '1em',
                                                    }}
                                                >
                                                    <ConditionnalZoomIconButton />
                                                    <StyTitle variant="h5">
                                                        {t(
                                                            'projection.expenseOrIncome',
                                                            {
                                                                expenseOrIncome:
                                                                    translateBlockValue(
                                                                        expIncome,
                                                                        true,
                                                                    ),
                                                            },
                                                        )}
                                                    </StyTitle>
                                                </Box>
                                                <GridComponent
                                                    {...tabItem.componentProps}
                                                    expenseOrIncome={expIncome}
                                                    excelExport={{
                                                        fileName:
                                                            getExcelFilename(
                                                                PROJECTION_FILE_PREFIX,
                                                                [
                                                                    projectionType ||
                                                                        'atterrissage',
                                                                ],
                                                            ),
                                                        sheetName:
                                                            getExpenseIncomeLabel(
                                                                expIncome,
                                                            ),
                                                    }}
                                                />
                                            </CardContent>
                                        </FullScreenProvider>
                                    </Card>
                                </Box>
                            );
                        })}
                    </StyGrids>
                </StyContentWrapper>
            ),
    }));

    return (
        <AuxiliaryTabs
            tabsList={tabsList}
            sx={{ '& #auxiliary-tab-header': { mx: '2rem' } }}
        />
    );
};

export default Projection;
